import React, { useCallback, useEffect, useState } from "react"
import { useHistory, Link } from "react-router-dom"
import { gql, useQuery } from "@apollo/client"

import Layout from "components/Layout"

import useInfiniteScroll from "hooks/useInfiniteScroll"

import formatDate from "utils/formatDate"
import groupBy from "lodash/groupBy"
import isPast from "date-fns/isPast"

export const TRIPS = gql`
  query Trips(
    $filter: TripFilterInput
    $page: Int
    $size: Int
    $sort: TripSortInput
  ) {
    trips(filter: $filter, page: $page, size: $size, sort: $sort) {
      id
      name
      url
      code
      startDate
      endDate
      photoURL
      image {
        url
      }

      agents {
        displayName
      }
      leaders {
        displayName
      }
    }
  }
`

const size = 30

const GroupsPage = () => {
  const history = useHistory()

  const { data, loading, error, fetchMore } = useQuery(TRIPS, {
    variables: {
      page: 0,
      size,
      sort: { startDate: "DESC" },
      filter: { type: { eq: "GROUP" } },
    },
  })

  const hasMore = loading ? false : data?.trips?.length >= size

  const handleSearch = useCallback(
    (search) => {
      if (!fetchMore) return

      if (search?.trim() === "") {
        fetchMore({
          updateQuery: (
            previousResult,
            { fetchMoreResult, queryVariables },
          ) => {
            return {
              trips: [...fetchMoreResult.trips],
            }
          },
        })

        return
      }

      fetchMore({
        variables: { filter: { search } },
        updateQuery: (previousResult, { fetchMoreResult, queryVariables }) => {
          return {
            trips: [
              // ...previousResult.matches,
              ...fetchMoreResult.trips,
            ],
          }
        },
      })
    },
    [fetchMore],
  )

  const handleLoadMore = useCallback(
    (page) => {
      if (page === 0) return
      if (loading) return

      fetchMore({
        variables: { page },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult
          return Object.assign({}, previousResult, {
            trips: [...previousResult.trips, ...fetchMoreResult.trips],
          })
        },
      })
    },
    [fetchMore, loading],
  )

  return (
    <Layout title="Groups | GTR" isLoading={loading}>
      <BoxHeader
        onSearch={handleSearch}
        onClick={() => history.push(`/groups/create`)}
      />

      <Groups
        data={data?.trips}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
      />
    </Layout>
  )
}

export default GroupsPage

const BoxHeader = ({ onSearch, onClick }) => {
  const [query, setQuery] = useState("")

  useEffect(() => {
    onSearch && onSearch(query)
  }, [query])

  return (
    <header className="bg-gray-800 text-gray-100 pb-24">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-12 gap-4">
        <div className="col-span-3 flex items-center">
          <h1 className="font-black text-3xl">Groups</h1>
        </div>

        <div className="col-span-6 relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-4">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 fill-current text-gray-600"
            >
              <path d="M16.32 14.9l1.1 1.1c.4-.02.83.13 1.14.44l3 3a1.5 1.5 0 0 1-2.12 2.12l-3-3a1.5 1.5 0 0 1-.44-1.14l-1.1-1.1a8 8 0 1 1 1.41-1.41l.01-.01zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z" />
            </svg>
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            className="block w-full border border-transparent bg-gray-900 focus:outline-none focus:bg-white focus:border-gray-300 text-gray-900 rounded-lg pl-12 pr-4 py-2"
          />
        </div>

        <div className="col-span-3 flex justify-end items-center pl-8">
          <button
            onClick={onClick}
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Group
          </button>
        </div>
      </div>
    </header>
  )
}

const Groups = ({ data, hasMore, loading, onLoadMore }) => {
  const groupsByPeriod = groupBy(data, (group) => {
    return isPast(new Date(group.startDate)) ? "PAST" : "UPCOMING"
  })

  const upcomingGroups = groupsByPeriod["UPCOMING"]
  const pastGroups = groupsByPeriod["PAST"]

  const [page, loaderRef] = useInfiniteScroll({ hasMore })

  useEffect(() => {
    onLoadMore && onLoadMore(page)
  }, [page])

  return (
    <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
      <div className="relative -mt-16 z-30">
        <GroupList data={upcomingGroups} />

        {pastGroups?.length > 0 && (
          <>
            <h2 className="text-2xl my-16">Past</h2>
            <GroupList data={pastGroups} />
          </>
        )}

        <div className="mt-96 mb-96"></div>
        {hasMore && (
          <div ref={loaderRef} className="text-center bg-gray-100">
            Loading…
          </div>
        )}
      </div>
    </main>
  )
}

const GroupList = ({ data }) => {
  return (
    <ul className="grid grid-cols-3 gap-8">
      {data?.map((group, index) => (
        <li key={index}>
          <Link to={`/groups/${group.id}`}>
            <div className="w-full">
              <div className="aspect-w-4 aspect-h-3 shadow-md">
                <img
                  src={group?.image?.url}
                  alt={group?.name}
                  className="w-full h-full rounded-lg object-cover"
                />
              </div>

              <div className="relative px-4 -mt-16">
                <div className="bg-white rounded-lg px-4 py-4 shadow-lg">
                  <div className="mt-1">
                    <p className="bg-blue-100 text-blue-800 px-2 py-1 inline-block leading-none rounded-full font-semibold uppercase tracking-wide text-xs">
                      {group?.code}
                    </p>
                  </div>

                  <h4 className=" text-gray-900 text-base line-clamp-1">
                    {group?.name}
                  </h4>

                  <div className="mt-1">
                    <div className="text-xs text-gray-600 font-semibold uppercase tracking-wide">
                      {`${formatDate(
                        group?.startDate,
                        "dd. MMM",
                      )} - ${formatDate(group?.endDate, "dd. MMM yyyy")}`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  )
}
